import { Button, Modal, useToast } from "@design";
import { type ClipboardEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppForm } from "@/components/form/form";
import { Text } from "@/components/Text";
import {
  acceptScreenshots,
  DESCRIPTION_MAX_LENGTH,
  type FeedbackCategory,
  feedbackSchema,
  MAX_SCREENSHOTS,
  type Screenshot,
  type ScreenshotRejection,
  TITLE_MAX_LENGTH,
  truncateContext,
} from "./feedbackForm";
import { pageCaptureSlot, reservedPlaces } from "./pageCaptureRules";
import { ScreenshotsField } from "./ScreenshotsField";
import { usePageCapture } from "./usePageCapture";
import { useFeedbackErrorMessage, useSubmitFeedback } from "./useSubmitFeedback";

/** The images on the clipboard, and whether it also carries text (browsers and screenshot tools often add both). */
function pasted(event: ClipboardEvent): { images: File[]; hasText: boolean } {
  const items = Array.from(event.clipboardData?.items ?? []);
  const images = items
    .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null);
  return { images, hasText: items.some((item) => item.kind === "string" && item.type === "text/plain") };
}

/**
 * Report a bug or suggest a feature, with optional screenshots (docs/spec/shell/feedback.md).
 * `pageUrl` and `capture` (its screenshot, from startPageCapture) are the page the dialog was opened
 * on. Mount only while open: closing discards the input.
 */
export function FeedbackModal({
  pageUrl,
  capture,
  onClose,
}: Readonly<{ pageUrl: string; capture: Promise<File | null>; onClose: () => void }>) {
  const { t } = useTranslation("feedback");
  const toast = useToast();
  const submit = useSubmitFeedback();
  const message = useFeedbackErrorMessage();
  const pageCapture = usePageCapture(capture);
  const [rejected, setRejected] = useState<ScreenshotRejection | null>(null);
  // Closed (Cancel, Escape) while sending: the send still finishes, but mustn't close a dialog opened since.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const form = useAppForm({
    defaultValues: {
      category: "bug" as FeedbackCategory,
      title: "",
      description: "",
      screenshots: [] as Screenshot[],
    },
    validators: {
      onChange: feedbackSchema({
        titleRequired: t("validation.titleRequired"),
        titleTooLong: t("validation.titleTooLong", { max: TITLE_MAX_LENGTH }),
        descriptionRequired: t("validation.descriptionRequired"),
        descriptionTooLong: t("validation.descriptionTooLong", { max: DESCRIPTION_MAX_LENGTH }),
      }),
    },
    onSubmit: async ({ value }) => {
      try {
        const screenshots = await pageCapture.withPageCapture(
          value.category,
          value.screenshots.map((s) => s.file),
        );
        await submit.mutateAsync({
          body: {
            category: value.category,
            title: value.title.trim(),
            description: value.description.trim(),
            page_url: truncateContext(pageUrl),
            user_agent: truncateContext(globalThis.navigator.userAgent),
            screenshots,
          },
          throwOnError: true,
        });
        toast.success(t(value.category === "bug" ? "sentBug" : "sentFeature"));
        if (mounted.current) onClose();
      } catch (error) {
        toast.error(message(error));
      }
    },
  });

  const addScreenshots = (incoming: File[]) => {
    if (incoming.length === 0) return;
    const existing = form.getFieldValue("screenshots");
    const slot = pageCaptureSlot(pageCapture.state, form.getFieldValue("category"), existing.length);
    const result = acceptScreenshots(existing, incoming, MAX_SCREENSHOTS - reservedPlaces(slot));
    form.setFieldValue("screenshots", result.screenshots);
    setRejected(result.rejected);
  };
  const removeScreenshot = (id: number) => {
    form.setFieldValue("screenshots", (screenshots) => screenshots.filter((s) => s.id !== id));
    setRejected(null);
  };
  const removePageCapture = () => {
    pageCapture.remove();
    setRejected(null);
  };
  const send = () => {
    if (!form.state.isSubmitting) void form.handleSubmit();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t("title")}
      closeLabel={t("close")}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("cancel")}
          </Button>
          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(submitting) => (
              <Button variant="primary" loading={submitting} onClick={send}>
                {t("send")}
              </Button>
            )}
          </form.Subscribe>
        </>
      }
    >
      <form
        aria-label={t("title")}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        onPaste={(e) => {
          const { images, hasText } = pasted(e);
          if (images.length === 0) return;
          // Text pastes as usual into the focused field; only an image-only paste is ours alone.
          if (!hasText) e.preventDefault();
          addScreenshots(images);
        }}
      >
        <Text tone="secondary">{t("intro")}</Text>
        <form.AppField name="category">
          {(field) => (
            <field.RadioGroupField
              label={t("category.label")}
              options={[
                { value: "bug", label: t("category.bug"), description: t("category.bugHint") },
                { value: "feature", label: t("category.feature"), description: t("category.featureHint") },
              ]}
            />
          )}
        </form.AppField>
        <form.Subscribe selector={(s) => s.values.category}>
          {(category) => (
            <>
              <form.AppField name="title">
                {(field) => (
                  <field.TextField
                    label={t("fields.title")}
                    placeholder={t(
                      category === "bug" ? "fields.titlePlaceholderBug" : "fields.titlePlaceholderFeature",
                    )}
                    maxLength={TITLE_MAX_LENGTH}
                    required
                  />
                )}
              </form.AppField>
              <form.AppField name="description">
                {(field) => (
                  <field.TextareaField
                    label={t(category === "bug" ? "fields.descriptionBug" : "fields.descriptionFeature")}
                    hint={t(category === "bug" ? "fields.descriptionHintBug" : "fields.descriptionHintFeature")}
                    rows={6}
                    required
                  />
                )}
              </form.AppField>
            </>
          )}
        </form.Subscribe>
        <form.Subscribe selector={(s) => [s.values.category, s.values.screenshots] as const}>
          {([category, screenshots]) => (
            <ScreenshotsField
              screenshots={screenshots}
              pageCapture={{
                slot: pageCaptureSlot(pageCapture.state, category, screenshots.length),
                file: pageCapture.state.result ?? null,
                onRemove: removePageCapture,
              }}
              rejected={rejected}
              onAdd={addScreenshots}
              onRemove={removeScreenshot}
            />
          )}
        </form.Subscribe>
      </form>
    </Modal>
  );
}
